import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Typography,
  Button,
  FormLabel,
  FormControl,
} from "@material-ui/core";
import { useLocation } from "react-router-dom";
import PageTitle from "../../components/pageTitle/PageTitle";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/Tabs/Tabs";
import AuthContext from "../../hooks/authContext";
import { packageAccess } from "../../services/helpers";
import { StorageLimitAccess } from "../../components/DialogComponent/SupplyDataDialog";
import AttachmentsView from "../../components/AttachmentsView/AttachmentsView";
import FileUploadComponent from "../../components/Fields/FileUploadComponent";
import { useMediaQuery } from "@mui/material";
import {
  SaveSupportTicket,
  uploadAttachment,
} from "../../services/apiservices/support";
import CommonContext from "../../hooks/commonContext";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textArea";
import { FilePlus2 } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import { Card, CardContent } from "../../components/ui/card";
import SingleSelectNew from "../../components/SelectField/SingleSelectNew";

const Support = () => {
  const [formdata, setFormData] = useState({});
  const isMobile = useMediaQuery("(max-width:767px)");
  const location = useLocation();
  const [attachments, setAttachments] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const auth = useContext(AuthContext);
  const companyId =
    auth?.userProfile?.role === "companyUser"
      ? auth?.userProfile?.id
      : auth?.userProfile?.companyId;
  const userPackageId = auth?.userProfile?.prdId;
  const allowedPermissions = packageAccess(userPackageId);
  // Navigate not used in this component
  // const navigate = useNavigate();
  const commonContext = useContext(CommonContext);
  const { toast } = useToast();

  useEffect(() => {
    if (location.state?.ticketType) {
      setActiveTab(location?.state?.ticketType);
    } else {
      setActiveTab("general");
    }
  }, [location.state, location.pathname]);

  const options = [
    { value: "Product Question", label: "Product Question" },
    { value: "Feature Request", label: "Feature Request" },
    { value: "General Question", label: "General Question" },
    { value: "Billing Question", label: "Billing Question" },
    { value: "Other", label: "Other" },
  ];
  const options1 = [
    { value: "Web Portal Question", label: "Web Portal Question" },
    { value: "Mobile App Question", label: "Mobile App Question" },
    { value: "Web Portal Bug ", label: "Web Portal Bug" },
    { value: "Mobile App Bug", label: "Mobile App Bug" },
  ];

  const handleInputChange = (event) => {
    const {
      target: { name, value },
    } = event;
    setFormData({ ...formdata, [name]: value });
  };


  const handleSelectChange = (value, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onFileUpload = async (files) => {
    const maxFileSize = 5 * 1024 * 1024;
    const extraLimit = auth?.userProfile?.additionalQuantity * 5;
    const extraLimitInBytes = extraLimit * 1024 ** 3;
    const storageLimit =
      (allowedPermissions?.storageLimits ?? 0) + extraLimitInBytes;

    let validFiles = [];

    files?.forEach((file) => {
      if (file.size > maxFileSize) {
        setErrorMessage(`You cannot upload files larger than 5 MB.`);
      } else if (auth?.userProfile?.storage + file.size > storageLimit) {
        setDialogOpen(true);
      } else {
        validFiles.push(file);
      }
    });

    setAttachments((prevAttachments) => {
      const newAttachments = [...prevAttachments, ...validFiles];
      if (newAttachments.length > 5) {
        setErrorMessage("You can upload a maximum of 5 attachments.");
        return prevAttachments;
      }
      return newAttachments;
    });
  };

  const handleDelete = (fileToDelete) => {
    setAttachments((prevAttachments) => {
      return prevAttachments.filter((file) => file !== fileToDelete);
    });
  };

  const onSaveChannel = async () => {
    try {
      commonContext?.setLoader(true);
      let attachmentUrls = [];

      if (attachments.length > 0) {
        attachmentUrls = await Promise.all(
          attachments.map((file) => uploadAttachment(companyId, file))
        );
      }

      const data = {
        type: activeTab,
        status: "open",
        subject: formdata?.subject,
        attachments: attachmentUrls,
        description: formdata?.description,
        companyId: companyId,
        read: false,
        portal: "web",
        categoryTypes: formdata?.categoryTypes
      };

      const email = auth?.userProfile?.email;
      const res = await SaveSupportTicket({data, email});

      if (res?.status === true) {
        toast?.({title: "Success", description: `${activeTab?.charAt(0).toUpperCase() + activeTab?.slice(1)} Support Ticket Created Successfully`});
        setFormData({ subject: "", description: "" });
        setAttachments([]);
        setActiveTab("general");
      }

      commonContext?.setLoader(false);
    } catch (error) {
      console.error("Failed to save support ticket:", error);
      toast?.({title: "Success", description: `Failed to Create Support Ticket`});
      commonContext?.setLoader(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  return (
    <>
      <Container>
        <PageTitle
          pageTitle="Create Support Ticket"
          showBreadcrumb={[{ label: "Home", to: "/" }, { label: "Supports", to: "/help/manageSupport" }]}
          BreadCrumbCurrentPageTitle={"Create Ticket"}
          showLastSeen={true}
        />
        <div className={isMobile ? "pv-18 ph-5" : "pv-24 ph-32"}>
          <Card className="dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] dark:text-white">
            <CardContent>
              <div className="p-24">
                <Tabs
                  key={activeTab}
                  defaultValue={activeTab}
                  onValueChange={(value) => setActiveTab(value)}
                  className="w-full"
                >
                  <TabsList>
                    <TabsTrigger value="general">General Support</TabsTrigger>
                    <TabsTrigger value="technical">
                      Technical Support
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="general">
                    <div className="mv-30">
                      <h2
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        General Support Request
                      </h2>
                      <p style={{ fontSize: "14px", color: "#6B7280" }}>
                        Use this form for sales inquiries, product questions, or
                        general requests.
                      </p>
                    </div>

                    <Typography
                      variant="body"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      Subject
                    </Typography>
                    <div className="mb-20 mt-10">
                      <Input
                        placeholder="Brief description of your inquiry"
                        size="small"
                        value={formdata?.subject}
                        fullWidth={true}
                        variant="outlined"
                        className={`field`}
                        name={"subject"}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Typography
                      variant="body"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      Type
                    </Typography>
                    <div className="mb-20 mt-10">
                      <SingleSelectNew
                        placeholder="Type"
                        size="small"
                        value={formdata?.categoryTypes}
                        fullWidth={true}
                        variant="outlined"
                        className={`field`}
                        name={"categoryTypes"}
                        options={options}
                        onChange={(value) => handleSelectChange(value, "categoryTypes")}
                      />
                    </div>
                    <Typography
                      variant="body"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      Description
                    </Typography>
                    <div className="mb-20 mt-10">
                      <Textarea
                        fullWidth={true}
                        name="description"
                        id="description"
                        multiline
                        rows={4}
                        variant="outlined"
                        value={formdata?.description}
                        onChange={handleInputChange}
                        placeholder="Please provide details about your inquiry or issue"
                      />
                    </div>
                    <div className="mb-20 mt-10">
                      <FormControl fullWidth className="mv-15">
                        <FormLabel
                          className="mr-20 mb-10 strong dark:text-white"
                        >
                          Attachments (Optional)
                        </FormLabel>
                        <AttachmentsView
                          attachments={attachments}
                          onDelete={handleDelete}
                        />
                        <FileUploadComponent
                          attachments={attachments}
                          onuploadfile={onFileUpload}
                          onlyImages={true}
                          buttonText={
                            <span className="d-flex flex-center">
                              <FilePlus2 className="h-4 w-4 mr-4" /> Add Files
                            </span>
                          }
                        />
                        <span className="mt-10" style={{ color: "#64748B" }}>
                          Upload screenshots to help us understand your request
                          (max 5MB per file).
                        </span>
                      </FormControl>
                      {errorMessage && (
                        <div style={{ color: "red", marginTop: "10px" }}>
                          {errorMessage}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ borderRadius: "4px", boxShadow: "none" }}
                      className={
                        isMobile
                          ? "themebutton mr-12 ph-20 pv-8"
                          : "themebutton flatbutton mr-12"
                      }
                      onClick={onSaveChannel}
                    >
                      Submit Ticket
                    </Button>
                  </TabsContent>
                  <TabsContent value="technical">
                    <div className="mv-30">
                      <h2
                        style={{
                          fontSize: "20px",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        Technical Support Request
                      </h2>
                      <p style={{ fontSize: "14px", color: "#6B7280" }}>
                        Use this form to report bugs, errors, or other technical
                        problems.
                      </p>
                    </div>
                    <Typography
                      variant="body"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      Subject
                    </Typography>
                    <div className="mb-20 mt-10">
                      <Input
                        placeholder="Brief description of your inquiry"
                        size="small"
                        value={formdata?.subject}
                        fullWidth={true}
                        variant="outlined"
                        className={`field`}
                        name={"subject"}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Typography
                      variant="body"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      Type
                    </Typography>
                    <div className="mb-20 mt-10">
                      <SingleSelectNew
                        placeholder="Type"
                        size="small"
                        value={formdata?.categoryTypes}
                        fullWidth={true}
                        variant="outlined"
                        className={`field`}
                        name={"categoryTypes"}
                        options={options1}
                        onChange={(value) => handleSelectChange(value, "categoryTypes")}
                      />
                    </div>
                    <Typography
                      variant="body"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                      }}
                    >
                      Description
                    </Typography>
                    <div className="mb-20 mt-10">
                      <Textarea
                        fullWidth={true}
                        name="description"
                        id="description"
                        multiline
                        rows={4}
                        variant="outlined"
                        value={formdata?.description}
                        onChange={handleInputChange}
                        placeholder="Please provide details about your inquiry or issue"
                      />
                    </div>
                    <div className="mb-20 mt-10">
                      <FormControl fullWidth className="mv-15">
                        <FormLabel
                          className="mr-20 mb-10 strong dark:text-white"
                        >
                          Attachments (Optional)
                        </FormLabel>
                        <AttachmentsView
                          attachments={attachments}
                          onDelete={handleDelete}
                        />
                        <FileUploadComponent
                          attachments={attachments}
                          onuploadfile={onFileUpload}
                          onlyImages={true}
                          buttonText={
                            <span className="d-flex flex-center">
                              <FilePlus2 className="h-4 w-4 mr-4" /> Add Files
                            </span>
                          }
                        />
                        <span className="mt-10" style={{ color: "#64748B" }}>
                          Upload screenshots to help us understand your request
                          (max 5MB per file).
                        </span>
                      </FormControl>
                      {errorMessage && (
                        <div style={{ color: "red", marginTop: "10px" }}>
                          {errorMessage}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="contained"
                      color="primary"
                      style={{ borderRadius: "4px", boxShadow: "none" }}
                      className={
                        isMobile
                          ? "themebutton mr-12 ph-20 pv-8"
                          : "themebutton flatbutton mr-12"
                      }
                      onClick={onSaveChannel}
                    >
                      Submit Ticket
                    </Button>
                  </TabsContent>
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </div>
      </Container>
      {dialogOpen && (
        <StorageLimitAccess
          open={dialogOpen}
          onClose={handleDialogClose}
          auth={auth}
        />
      )}
    </>
  );
};

export default Support;
